import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MediaDropzone from "@/components/admin/MediaDropzone";

export default function AdminHeroMedia() {
  const [hero, setHero] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/hero").then((r) => setHero(r.data?.data || {})).catch(() => {});
  }, []);

  const update = (patch) => setHero((h) => ({ ...h, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/hero", { data: hero });
      toast.success("Hero media saved · refresh the site to see it");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!hero) return <div className="text-charcoal/40">Loading…</div>;

  return (
    <div data-testid="admin-hero-media-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Hero</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Hero media</h1>
        <p className="text-charcoal/65 font-light mt-2 max-w-xl">
          Upload the cinematic hero video (autoplay, muted, looped) and a poster image as fallback.
          If a video is set, it plays automatically; the poster image shows while the video loads.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl">
        <section className="bg-white border border-beige p-5 md:p-6" data-testid="hero-video-section">
          <div className="font-display text-xl text-charcoal mb-1">Hero video</div>
          <p className="text-xs text-charcoal/55 mb-4">MP4 / WebM · ≤150 MB · short loop recommended (10–20s)</p>
          <MediaDropzone
            folder="mostash/hero"
            accept="video"
            height="aspect-video"
            label=""
            testid="hero-video-dropzone"
            value={hero.video_url ? { url: hero.video_url, public_id: hero.video_public_id, resource_type: "video" } : null}
            onChange={(asset) => {
              if (asset) update({ video_url: asset.url, video_public_id: asset.public_id });
              else update({ video_url: "", video_public_id: "" });
            }}
          />
        </section>

        <section className="bg-white border border-beige p-5 md:p-6" data-testid="hero-poster-section">
          <div className="font-display text-xl text-charcoal mb-1">Hero poster image</div>
          <p className="text-xs text-charcoal/55 mb-4">Used when no video is set, or as the video&apos;s loading frame.</p>
          <MediaDropzone
            folder="mostash/hero"
            accept="image"
            height="aspect-video"
            label=""
            testid="hero-poster-dropzone"
            value={hero.poster_image ? { url: hero.poster_image, public_id: hero.poster_public_id, resource_type: "image" } : null}
            onChange={(asset) => {
              if (asset) update({ poster_image: asset.url, poster_public_id: asset.public_id });
              else update({ poster_image: "", poster_public_id: "" });
            }}
          />
        </section>
      </div>

      <div className="mt-8 max-w-5xl bg-white border border-beige p-5 md:p-6">
        <div className="font-display text-xl text-charcoal mb-4">Hero copy</div>
        <label className="block mb-4">
          <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Eyebrow label</span>
          <input className="mt-2 field-input" value={hero.label || ""} onChange={(e) => update({ label: e.target.value })} data-testid="hero-label" />
        </label>
        <label className="block mb-4">
          <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Headline</span>
          <input className="mt-2 field-input" value={hero.headline || ""} onChange={(e) => update({ headline: e.target.value })} data-testid="hero-headline-input" />
        </label>
        <label className="block">
          <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Subheadline</span>
          <textarea rows={3} className="mt-2 field-textarea" value={hero.subheadline || ""} onChange={(e) => update({ subheadline: e.target.value })} data-testid="hero-subheadline-input" />
        </label>
      </div>

      <div className="mt-6">
        <button type="button" onClick={save} disabled={saving} data-testid="hero-save" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving</> : <><Save className="h-4 w-4" /> Save changes</>}
        </button>
      </div>
    </div>
  );
}
